/**
 * Kiểm tra xem một giá trị có được coi là "rỗng" hay không đối với mục đích validate.
 * @param {*} value Giá trị cần kiểm tra.
 * @param {string} fieldType Loại trường ('text', 'textarea', 'number', 'select', 'checkbox', etc.)
 * @returns {boolean} True nếu giá trị là rỗng, False nếu không.
 */
function isValueEmpty(value, fieldType) {
    if (value === null || value === undefined) {
        return true;
    }
    if (fieldType === 'text' || fieldType === 'textarea' || fieldType === 'select') {
        // Chuỗi rỗng hoặc chỉ chứa khoảng trắng được coi là rỗng
        return String(value).trim() === '';
    }
    // Đối với number, 0 có thể là giá trị hợp lệ, nên chỉ kiểm tra null/undefined (đã làm ở trên)
    // Đối với checkbox, giá trị false là hợp lệ
    // Đối với các loại khác, chỉ cần tồn tại là đủ
    return false;
}

/**
 * Kiểm tra xem một trường có nên được hiển thị dựa trên điều kiện showIf hay không.
 * @param {object} fieldMetadata Metadata của trường cần kiểm tra.
 * @param {object} nodeData Dữ liệu hiện tại của node.
 * @returns {boolean} True nếu trường nên được hiển thị, False nếu không.
 */
function shouldShowField(fieldMetadata, nodeData) {
    if (!fieldMetadata.showIf) {
        return true; // Hiển thị nếu không có điều kiện
    }
    // showIf dạng hàm (field, data) => boolean — PropertiesBar đã hỗ trợ, kiểm tra hợp lệ cũng phải
    // hỗ trợ, nếu không trường bắt buộc đang bị ẩn vẫn bị báo "thiếu".
    if (typeof fieldMetadata.showIf === 'function') {
        try { return !!fieldMetadata.showIf(fieldMetadata, nodeData || {}); } catch (e) { return true; }
    }
    const { field: dependencyField, value: dependencyValue } = fieldMetadata.showIf;
    const currentDependencyValue = nodeData ? nodeData[dependencyField] : undefined;

    if (Array.isArray(dependencyValue)) {
        return dependencyValue.includes(currentDependencyValue);
    } else {
        return currentDependencyValue === dependencyValue;
    }
}

/**
 * Kiểm tra dữ liệu của một flow definition dựa trên metadata của các loại node.
 *
 * @param {object} flowDefinition Đối tượng flow chứa mảng `nodes`. Ví dụ: { nodes: [...], edges: [...] }
 * @param {Array<object>} nodeDefinitions Mảng chứa định nghĩa metadata cho từng loại node.
 * @returns {Array<object>} Mảng các node không hợp lệ. Mỗi object chứa:
 * { nodeId: string, nodeName: string, nodeType: string, missingFields: Array<string> }
 */
function validateFlowData(flowDefinition, nodeDefinitions) {
    const invalidNodes = [];
    if (!flowDefinition || !Array.isArray(flowDefinition.nodes)) {
        console.error("Invalid flowDefinition format. Expected object with a 'nodes' array.");
        return invalidNodes; // Trả về mảng rỗng nếu định dạng flow không đúng
    }
    if (!Array.isArray(nodeDefinitions)) {
         console.error("Invalid nodeDefinitions format. Expected an array.");
         return invalidNodes; // Trả về mảng rỗng nếu định nghĩa node không đúng
    }

    // Tạo một map để truy cập nhanh metadata theo type
    const nodeMetadataMap = nodeDefinitions.reduce((map, nodeDef) => {
        if (nodeDef && nodeDef.type) {
            map[nodeDef.type] = nodeDef.metadata || [];
        }
        return map;
    }, {});

    // Lặp qua từng node trong flow cần kiểm tra
    flowDefinition.nodes.forEach(node => {
        if (!node || !node.type || !node.id) {
            console.warn("Node in flow is missing id or type:", node);
            // Có thể thêm lỗi vào invalidNodes nếu muốn
            return; // Bỏ qua node không hợp lệ
        }

        const nodeMetadataFields = nodeMetadataMap[node.type];
        const nodeData = node.data || {}; // Dữ liệu thực tế của node
        const missingFields = [];

        if (!nodeMetadataFields) {
            console.warn(`No metadata found for node type: ${node.type} (Node ID: ${node.id})`);
            // Có thể coi đây là lỗi nếu muốn
            // missingFields.push('Unknown Node Type');
        } else {
             /*// Kiểm tra trường 'name' (luôn yêu cầu)
             if (isValueEmpty(nodeData.name, 'text')) {
                 missingFields.push('Tên node');
             }*/

            // Lặp qua các trường trong metadata của loại node này
            nodeMetadataFields.forEach(field => {
                // Kiểm tra xem trường có nên hiển thị không
                if (shouldShowField(field, nodeData)) {
                    // Nếu trường là bắt buộc
                    if (field.required) {
                        // Kiểm tra xem giá trị có bị rỗng không
                        if (isValueEmpty(nodeData[field.name], field.type)) {
                            missingFields.push(field.label);
                        }
                    }
                }
            });
        }

        // Nếu có trường bắt buộc bị thiếu, thêm node này vào danh sách không hợp lệ
        if (missingFields.length > 0) {
            invalidNodes.push({
                nodeId: node.id,
                nodeName: nodeData.name || node.name || `(Node ${node.id})`, // Lấy tên hoặc ID
                nodeType: node.type,
                missingFields: missingFields
            });
        }
    });

    return invalidNodes;
}
export {validateFlowData,isValueEmpty,shouldShowField}